import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, inr, fmtIST } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import NewSkuModal from "../components/NewSkuModal";
import { Package, CheckCircle, Plus, Trash, MagnifyingGlass } from "@phosphor-icons/react";

export default function Inwarding() {
  const { storeId } = useParams();
  const [tab, setTab] = useState("warehouse"); // warehouse | vendor
  const [incoming, setIncoming] = useState([]);
  const [selected, setSelected] = useState(null);
  const [received, setReceived] = useState({});
  const [history, setHistory] = useState([]);

  // Vendor form
  const [vendor, setVendor] = useState({ vendor_name: "", vendor_gstin: "", invoice_no: "" });
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [vLines, setVLines] = useState([]);
  const [showNewSku, setShowNewSku] = useState(false);

  const load = async () => {
    const [inc, hist] = await Promise.all([
      api.get(`/store/${storeId}/incoming`),
      api.get(`/store/${storeId}/inwards`),
    ]);
    setIncoming(inc.data);
    setHistory(hist.data);
  };
  useEffect(() => { load(); }, [storeId]);

  const openDispatch = (d) => {
    setSelected(d);
    const init = {};
    for (const it of d.items) init[it.sku] = it.qty;
    setReceived(init);
  };

  const confirmMatch = async () => {
    await api.post("/store/inward/match", { dispatch_id: selected.id, received });
    setSelected(null); setReceived({});
    load();
  };

  // Vendor search
  const [searched, setSearched] = useState(false);
  const search = async () => {
    if (!q) return;
    const r = await api.get(`/warehouse/skus?q=${encodeURIComponent(q)}&limit=10`);
    setResults(r.data.items || []);
    setSearched(true);
  };
  const addV = (s) => {
    if (vLines.find((l) => l.sku === s.sku)) return;
    setVLines([...vLines, { sku: s.sku, product_name: s.name, barcode: s.barcode || "", hsn: s.hsn || "", gst_rate: s.gst_rate || 0, qty: 1, rate: s.selling_price || s.mrp || 0, thumbnail: s.thumbnail || "" }]);
    setQ(""); setResults([]);
  };
  const saveVendor = async () => {
    if (!vendor.vendor_name || vLines.length === 0) return alert("Vendor name and at least one item required.");
    await api.post("/store/inward/vendor", { store_id: storeId, ...vendor, items: vLines });
    setVendor({ vendor_name: "", vendor_gstin: "", invoice_no: "" });
    setVLines([]);
    load();
  };

  return (
    <Shell>
      <PageHeader title="Inwarding" subtitle="Receive stock from warehouse dispatch or directly from vendors." testId="inward-header" />
      <div className="p-8 space-y-4">
        <div className="inline-flex bg-white border border-slate-200 rounded-md p-1">
          {[["warehouse", "From Warehouse"], ["vendor", "From Vendor"], ["history", "History"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`inward-tab-${k}`} className={`px-4 h-9 text-xs font-semibold rounded-sm uppercase tracking-wider ${tab === k ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>

        {tab === "warehouse" && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="incoming-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-4 py-3 font-semibold">Invoice #</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Items</th>
                  <th className="text-right px-4 py-3 font-semibold">Total</th>
                  <th className="text-left px-4 py-3 font-semibold">E-Way</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {incoming.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400">No incoming dispatches.</td></tr>}
                {incoming.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono-num font-semibold">{d.invoice_no}</td>
                    <td className="px-4 py-3 font-mono-num text-slate-600">{fmtIST(d.created_at)}</td>
                    <td className="px-4 py-3 font-mono-num">{d.items.length}</td>
                    <td className="px-4 py-3 text-right font-mono-num">{inr(d.grand_total)}</td>
                    <td className="px-4 py-3 font-mono-num text-slate-600">{d.eway_bill_no || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openDispatch(d)} data-testid={`inward-open-${d.invoice_no}`} className="h-9 px-3 text-xs font-semibold bg-slate-900 text-white rounded-md hover:bg-slate-800">Match & Inward</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "vendor" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white border border-slate-200 rounded-md p-5 grid grid-cols-3 gap-3">
                <input value={vendor.vendor_name} onChange={(e) => setVendor({ ...vendor, vendor_name: e.target.value })} placeholder="Vendor name" data-testid="vendor-name" className="h-10 px-3 border border-slate-300 rounded-md text-sm" />
                <input value={vendor.vendor_gstin} onChange={(e) => setVendor({ ...vendor, vendor_gstin: e.target.value })} placeholder="Vendor GSTIN" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
                <input value={vendor.invoice_no} onChange={(e) => setVendor({ ...vendor, invoice_no: e.target.value })} placeholder="Vendor Invoice #" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
              </div>

              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Add Product from SKU Master</div>
                  <button onClick={() => setShowNewSku(true)} data-testid="open-new-sku" className="h-8 px-3 text-xs font-bold text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded-md inline-flex items-center gap-1"><Plus size={12} weight="bold" /> New SKU</button>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={q} onChange={(e) => { setQ(e.target.value); setSearched(false); }} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())} placeholder="Search SKU / name / barcode…" className="w-full h-10 pl-9 pr-3 border border-slate-300 rounded-md text-sm" />
                  </div>
                  <button onClick={search} className="h-10 px-4 bg-slate-900 text-white font-semibold rounded-md text-sm">Search</button>
                </div>
                {searched && results.length === 0 && (
                  <div className="mt-3 border border-dashed border-slate-300 rounded-md p-3 text-center text-sm text-slate-500" data-testid="no-sku-results">
                    No matching SKU found.{" "}
                    <button onClick={() => setShowNewSku(true)} className="font-bold text-emerald-700 hover:underline">Create "{q}" as a new SKU →</button>
                  </div>
                )}
                {results.length > 0 && (
                  <div className="mt-3 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-y-auto">
                    {results.map((s) => (
                      <button key={s.sku} onClick={() => addV(s)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3">
                        {s.thumbnail ? <img src={s.thumbnail} alt="" className="w-8 h-8 rounded-sm object-cover" /> : <div className="w-8 h-8 rounded-sm bg-slate-100" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{s.name}</div>
                          <div className="text-xs text-slate-500 font-mono-num">{s.sku}</div>
                        </div>
                        <Plus size={14} className="text-emerald-600" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Product</th>
                      <th className="text-right px-3 py-2 font-semibold">Qty</th>
                      <th className="text-right px-3 py-2 font-semibold">Rate</th>
                      <th className="text-right px-3 py-2 font-semibold">Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {vLines.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400 text-sm">No items added.</td></tr>}
                    {vLines.map((l, i) => (
                      <tr key={l.sku} className="border-b border-slate-100">
                        <td className="px-3 py-2">
                          <div className="font-medium text-sm">{l.product_name}</div>
                          <div className="text-[10px] text-slate-500 font-mono-num">{l.sku}</div>
                        </td>
                        <td className="px-3 py-2"><input type="number" value={l.qty} onChange={(e) => { const c = [...vLines]; c[i].qty = parseInt(e.target.value) || 0; setVLines(c); }} className="w-16 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" /></td>
                        <td className="px-3 py-2"><input type="number" step="0.01" value={l.rate} onChange={(e) => { const c = [...vLines]; c[i].rate = parseFloat(e.target.value) || 0; setVLines(c); }} className="w-24 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" /></td>
                        <td className="px-3 py-2 text-right font-mono-num">{inr(l.qty * l.rate)}</td>
                        <td className="px-3 py-2 text-right"><button onClick={() => setVLines(vLines.filter((_, idx) => idx !== i))} className="p-1 text-red-600 hover:bg-red-50 rounded-md"><Trash size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Summary</div>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Items</span><span className="font-mono-num">{vLines.length}</span></div>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Units</span><span className="font-mono-num">{vLines.reduce((a, l) => a + l.qty, 0)}</span></div>
                <div className="flex justify-between items-baseline border-t border-slate-200 pt-2 mt-2">
                  <span className="font-display font-bold">Total</span>
                  <span className="font-display font-bold text-lg font-mono-num">{inr(vLines.reduce((a, l) => a + l.qty * l.rate, 0))}</span>
                </div>
              </div>
              <button onClick={saveVendor} data-testid="save-vendor-inward" className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-md">Inward Stock</button>
            </div>
          </div>
        )}

        {tab === "history" && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Ref</th>
                  <th className="text-right px-4 py-3">Items</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-slate-400">No inwards yet.</td></tr>}
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-100">
                    <td className="px-4 py-2 font-mono-num">{fmtIST(h.created_at)}</td>
                    <td className="px-4 py-2 uppercase text-xs font-semibold">{h.type}</td>
                    <td className="px-4 py-2 font-mono-num text-slate-600">{h.dispatch_id || h.vendor_name}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{(h.items || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNewSku && (
        <NewSkuModal
          initialSku={q}
          ctaLabel="Create & Add to Inward"
          subtitle="Added to the SKU Master and to this inward in one step."
          onClose={() => setShowNewSku(false)}
          onCreated={(s) => {
            setShowNewSku(false);
            setQ(""); setResults([]); setSearched(false);
            if (!vLines.find((l) => l.sku === s.sku)) {
              setVLines((prev) => [...prev, {
                sku: s.sku, product_name: s.name, barcode: s.barcode || "", hsn: s.hsn || "",
                gst_rate: s.gst_rate || 0, qty: 1,
                rate: s.cost_price || s.selling_price || s.mrp || 0, thumbnail: "",
              }]);
            }
          }}
        />
      )}

      {selected && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-md shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="match-dispatch-modal">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-lg">Match Dispatch — {selected.invoice_no}</div>
                <div className="text-xs text-slate-500">Confirm received quantities and inward to store.</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-slate-100 rounded-md">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Product</th>
                    <th className="text-right px-3 py-2 font-semibold">Dispatched</th>
                    <th className="text-right px-3 py-2 font-semibold">Received</th>
                    <th className="text-right px-3 py-2 font-semibold">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.items.map((it) => {
                    const recv = received[it.sku] ?? it.qty;
                    const vari = recv - it.qty;
                    return (
                      <tr key={it.sku} className="border-b border-slate-100">
                        <td className="px-3 py-2">
                          <div className="font-medium">{it.product_name}</div>
                          <div className="text-[10px] text-slate-500 font-mono-num">{it.sku}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono-num">{it.qty}</td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" value={recv} onChange={(e) => setReceived({ ...received, [it.sku]: parseInt(e.target.value) || 0 })} data-testid={`recv-${it.sku}`} className="w-20 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" />
                        </td>
                        <td className={`px-3 py-2 text-right font-mono-num font-semibold ${vari === 0 ? "text-slate-400" : vari < 0 ? "text-red-600" : "text-emerald-600"}`}>{vari > 0 ? `+${vari}` : vari}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setSelected(null)} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={confirmMatch} data-testid="confirm-inward-btn" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md inline-flex items-center gap-1"><CheckCircle size={14} weight="bold" />Confirm Inward</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
